import { useRef, useState } from "react";
import imageCompression from "browser-image-compression";

import EnvConfig from "../config/env.config";
import { minutesFromNow } from "../utils/time";
import { CloudflareResource } from "../types/resources";
import { MediaAssetClient } from "../types/media.asset";
import { getCloudflareBatchToken } from "../apis/resources/get.cloudflare.batch.token";
import { useSnapshot } from "valtio";
import { editorState } from "../utils/editor";
import { createR2UploadUrl } from "../apis/media-asset/create.r2.upload.url";

let cachedCloudflareBatchToken: string | null = null;
let cachedCloudflareExpiresAt: Date | null = null;

export type UseImageUploadMeta = {
  field: string;
  collectionID: string;
  collection: string;
  mediaAssetClient: MediaAssetClient;
  handleMediaAssetID?: (mediaAssetID: string) => void;
};
const useImageUpload = ({
  field,
  collectionID,
  collection,
  handleMediaAssetID,
  mediaAssetClient,
}: UseImageUploadMeta) => {
  const { XAccountHash } = useSnapshot(editorState);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setUploading(false);
      setUploadSuccess(null);
    }
  };

  const uploadImage = async (
    file: File,
    setImage: (image: string) => void,
  ): Promise<string | null> => {
    setUploadSuccess(null);
    setUploading(true);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      // use Cloudflare Images for dev and R2 for prod
      if (
        EnvConfig.appEnv === "production" &&
        mediaAssetClient !== MediaAssetClient.MediaAssetClientCloudflare
      ) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          initialQuality: 0.85,
          fileType: "image/webp",
        };

        const compressedFile = await imageCompression(file, options);
        const contentType = compressedFile.type || "image/webp";

        const getUploadUrl = (): Promise<{
          uploadUrl: string;
          publicUrl: string;
          mediaAssetID: string;
        }> => {
          return new Promise((resolve, reject) => {
            createR2UploadUrl({
              headers: {
                "X-Account-Hash": XAccountHash || "",
              },
              body: {
                field,
                collectionID,
                collection,
                contentType,
                originalFileName: file.name,
              },
              onCompleted(res) {
                resolve(res.data);
              },
              onError(err) {
                console.error(err);
                reject(err);
              },
            });
          });
        };

        const { uploadUrl, publicUrl, mediaAssetID } = await getUploadUrl();
        const client = new AbortController();
        abortControllerRef.current = client;
        const res = await fetch(uploadUrl, {
          method: "PUT",
          body: compressedFile,
          headers: {
            "Content-Type": contentType,
          },
          signal: client.signal,
        });
        if (!res.ok) {
          throw new Error("Failed to upload image");
        }

        handleMediaAssetID?.(mediaAssetID);
        setImage(publicUrl);
        setUploading(false);
        setUploadSuccess(true);
        return publicUrl;
      } else {
        let cloudflareBatchToken = cachedCloudflareBatchToken;
        let cloudflareExpiresAt = cachedCloudflareExpiresAt;
        const shouldFetchToken =
          !cloudflareBatchToken ||
          !cloudflareExpiresAt ||
          cloudflareExpiresAt < minutesFromNow(-5);
        if (shouldFetchToken) {
          const getToken = (): Promise<CloudflareResource> => {
            return new Promise((resolve, reject) => {
              getCloudflareBatchToken(
                {
                  headers: {
                    "X-Account-Hash": XAccountHash,
                  },
                  onCompleted(res) {
                    cachedCloudflareBatchToken = res.data.batchToken;
                    cachedCloudflareExpiresAt = new Date(res.data.expiresAt);
                    resolve(res.data);
                  },
                  onError(err) {
                    console.error(err);
                    reject(err);
                  },
                  // never cache this. -- we're already syncing with l.storage --
                },
                { bypassCache: true },
              );
            });
          };
          const { batchToken, expiresAt } = await getToken();
          cloudflareBatchToken = batchToken;
          cloudflareExpiresAt = expiresAt;
          cachedCloudflareBatchToken = batchToken;
          cachedCloudflareExpiresAt = expiresAt;
        }

        // high compression factor
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          initialQuality: 0.4,
        };

        const compressedFile = await imageCompression(file, options);
        const formData = new FormData();
        formData.append("file", compressedFile);
        const response = await fetch(
          "https://batch.imagedelivery.net/images/v1",
          {
            method: "POST",
            body: formData,
            headers: {
              Authorization: "Bearer " + cloudflareBatchToken,
            },
            signal: abortControllerRef.current.signal,
          },
        );

        if (!response.ok) {
          throw new Error("Failed to upload image");
        }

        const data = (await response.json()) as {
          result: {
            variants: Array<string>;
          };
          success: boolean;
          errors: [];
          messages: [];
        };

        // Ensures we are always using the public variant of the image
        const publicVariant =
          data?.result?.variants?.find((url) => url.endsWith("/public")) || "";
        setImage(publicVariant);
        setUploading(false);
        setUploadSuccess(true);
        return publicVariant;
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setUploading(false);
      setUploadSuccess(false);
      return null;
    }
  };

  return { uploading, uploadImage, uploadSuccess, cancelUpload };
};

export default useImageUpload;
